import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Landmark, Copy, Loader2, Banknote, Wallet, CheckCircle2 } from "lucide-react";
import { api, formatPrice, IBAN_INFO, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const METHODS = [
  { id: "card", label: "Carte bancaire", icon: CreditCard },
  { id: "cash", label: "Espèces", icon: Banknote },
  { id: "transfer", label: "Virement", icon: Landmark },
  { id: "paypal", label: "PayPal", icon: Wallet },
];

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const { user, loading: authLoading, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.info("Connectez-vous pour finaliser votre commande");
      navigate("/connexion", { state: { from: "/paiement" } });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !delivery) {
      setDelivery({
        phone: user.phone || "",
        address_line: user.address_line || "",
        postal_code: user.postal_code || "",
        city: user.city || "",
        building: user.building || "",
        building_code: user.building_code || "",
        country: user.country || "France",
      });
    }
  }, [user, delivery]);

  useEffect(() => {
    if (items.length === 0 && !confirmResult) navigate("/panier");
  }, [items.length, navigate, confirmResult]);

  if (authLoading || !user || !delivery || (items.length === 0 && !confirmResult)) return <div className="pt-40 text-center text-zinc-500">Chargement…</div>;

  const upDelivery = (k) => (e) => setDelivery({ ...delivery, [k]: e.target.value });

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await updateProfile(delivery);
      toast.success("Coordonnées de livraison enregistrées");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingInfo(false);
    }
  };

  const validDelivery = () => {
    if (!delivery.address_line || !delivery.city || !delivery.postal_code) {
      toast.error("Veuillez renseigner votre adresse de livraison complète");
      return false;
    }
    return true;
  };

  const handleCardPayment = async () => {
    if (!validDelivery()) return;
    setLoading(true);
    try {
      await updateProfile(delivery);
      const res = await api.post("/checkout/session", {
        items: items.map((i) => ({ artwork_id: i.id, quantity: 1 })),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const handleCoordination = async () => {
    if (!validDelivery()) return;
    setLoading(true);
    try {
      const res = await api.post("/checkout/confirm-coordination", {
        items: items.map((i) => ({ artwork_id: i.id, quantity: 1 })),
        payment_method: method,
        name: user.name, ...delivery,
      });
      setConfirmResult(res.data);
      clearCart();
      toast.success("Commande enregistrée ! Le(s) artiste(s) ont été prévenus.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text.replace(/\s/g, ""));
    toast.success("Copié dans le presse-papier");
  };

  if (confirmResult) {
    const m = confirmResult.payment_method;
    return (
      <div className="pt-32 pb-24 max-w-3xl mx-auto px-6 lg:px-10 min-h-[100svh]" data-testid="checkout-confirmed">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle2 className="h-8 w-8 text-emerald-400" />
          <h1 className="font-display text-4xl text-white">Commande enregistrée</h1>
        </div>
        <p className="text-zinc-300 mb-6">
          Total : <span className="text-white">{formatPrice(confirmResult.total)}</span>. L'artiste a reçu vos coordonnées et vous contactera.
        </p>
        {m === "transfer" && (
          <div className="border border-white/15 bg-[#0d0d0d] p-6" data-testid="coordination-iban">
            <p className="text-zinc-300 text-sm mb-4">Effectuez le virement vers le compte de l'artiste (indiquez votre nom en référence) :</p>
            {confirmResult.sellers && confirmResult.sellers.length > 0 ? (
              confirmResult.sellers.map((s, i) => (
                <div key={s.iban || s.name} className="mb-4 pb-4 border-b border-white/10 last:border-0">
                  <p className="text-white text-sm mb-2">{s.name} <span className="text-zinc-500">— {s.items.join(", ")}</span></p>
                  <IbanRow label="IBAN" value={s.iban} onCopy={() => copy(s.iban)} />
                </div>
              ))
            ) : (
              <>
                <p className="text-zinc-400 text-xs mb-3">Coordonnées de la Fondation :</p>
                <IbanRow label="Titulaire" value={IBAN_INFO.holder} />
                <IbanRow label="IBAN" value={IBAN_INFO.iban} onCopy={() => copy(IBAN_INFO.iban)} />
                <IbanRow label="BIC" value={IBAN_INFO.bic} onCopy={() => copy(IBAN_INFO.bic)} />
              </>
            )}
          </div>
        )}
        {(m === "cash" || m === "paypal") && (
          <p className="text-zinc-300">
            {m === "cash"
              ? "Réglez en espèces directement avec l'artiste : contactez-le via la messagerie pour convenir des modalités."
              : "Contactez l'artiste via la messagerie pour obtenir son numéro/email PayPal et régler la commande."}
          </p>
        )}
        <div className="flex gap-4 mt-8">
          <button data-testid="go-messages" onClick={() => navigate("/messagerie")} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 text-sm transition-colors">Ouvrir la messagerie</button>
          <button onClick={() => navigate("/galerie")} className="border border-white/20 hover:border-white text-white px-6 py-3 text-sm transition-colors">Continuer mes achats</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 max-w-5xl mx-auto px-6 lg:px-10 min-h-screen" data-testid="checkout-page">
      <h1 className="font-display text-5xl text-white tracking-tight mb-12">Paiement</h1>

      <div className="grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3 space-y-8">
          {/* Customer info */}
          <div>
            <h2 className="text-sm uppercase tracking-widest text-zinc-500 mb-5">Livraison</h2>
            <div className="border border-white/15 bg-[#0d0d0d] p-5 space-y-4" data-testid="checkout-customer">
              <p className="text-white text-sm">{user.name} · <span className="text-zinc-400">{user.email}</span></p>
              <CheckoutField testid="checkout-phone" label="Téléphone" value={delivery.phone} onChange={upDelivery("phone")} placeholder="Ex. 06 12 34 56 78" />
              <CheckoutField testid="checkout-address" label="Adresse" value={delivery.address_line} onChange={upDelivery("address_line")} placeholder="N° et nom de rue" />
              <div className="grid grid-cols-2 gap-4">
                <CheckoutField testid="checkout-postal" label="Code postal" value={delivery.postal_code} onChange={upDelivery("postal_code")} />
                <CheckoutField testid="checkout-city" label="Ville" value={delivery.city} onChange={upDelivery("city")} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CheckoutField testid="checkout-building" label="Bâtiment / Étage" value={delivery.building} onChange={upDelivery("building")} />
                <CheckoutField testid="checkout-building-code" label="Code d'entrée / interphone" value={delivery.building_code} onChange={upDelivery("building_code")} />
              </div>
              <CheckoutField testid="checkout-country" label="Pays" value={delivery.country} onChange={upDelivery("country")} />
              <button onClick={saveInfo} disabled={savingInfo} data-testid="save-delivery-info"
                className="text-[#60a5fa] text-xs hover:underline disabled:opacity-60">
                {savingInfo ? "Enregistrement…" : "Enregistrer mes coordonnées"}
              </button>
            </div>
          </div>

          {/* Payment method */}
          <div>
            <h2 className="text-sm uppercase tracking-widest text-zinc-500 mb-5">Mode de paiement</h2>
            <div className="grid grid-cols-2 gap-4">
              {METHODS.map((mm) => {
                const Icon = mm.icon;
                return (
                  <button
                    key={mm.id}
                    data-testid={`method-${mm.id}`}
                    onClick={() => setMethod(mm.id)}
                    className={`flex items-center gap-3 p-4 border transition-all ${
                      method === mm.id ? "border-[#2563EB] bg-[#2563EB]/10 text-white" : "border-white/15 text-zinc-400 hover:border-white/40"
                    }`}
                  >
                    <Icon className="h-5 w-5" /> {mm.label}
                  </button>
                );
              })}
            </div>
          </div>

          {method === "card" ? (
            <button
              data-testid="pay-card-button"
              onClick={handleCardPayment}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-10 py-4 text-sm tracking-wide transition-colors"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirection…</> : <>Payer {formatPrice(total)} par carte</>}
            </button>
          ) : (
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm leading-relaxed">
                {method === "transfer" && "En validant, l'artiste sera prévenu et vous recevrez ses coordonnées bancaires (IBAN) pour effectuer le virement."}
                {method === "cash" && "En validant, l'artiste sera prévenu ; réglez ensuite en espèces directement avec lui (contact via la messagerie)."}
                {method === "paypal" && "En validant, l'artiste sera prévenu ; contactez-le via la messagerie pour obtenir son PayPal."}
              </p>
              <button
                data-testid="confirm-coordination-button"
                onClick={handleCoordination}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-10 py-4 text-sm tracking-wide transition-colors"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Traitement…</> : <>Valider ma commande ({formatPrice(total)})</>}
              </button>
            </div>
          )}
        </div>

        {/* Order summary */}
        <div className="lg:col-span-2">
          <div className="border border-white/15 bg-[#0d0d0d] p-6 lg:sticky lg:top-28">
            <h2 className="font-display text-2xl text-white mb-6">Récapitulatif</h2>
            <div className="space-y-4 mb-6">
              {items.map((i) => (
                <div key={i.id} className="flex gap-4">
                  <img src={i.image_url} alt={i.title} className="h-16 w-14 object-cover" />
                  <div className="flex-1">
                    <p className="text-white text-sm leading-tight">{i.title}</p>
                    <p className="text-zinc-500 text-xs">{i.artist_name}</p>
                  </div>
                  <span className="text-zinc-300 text-sm">{formatPrice(i.price)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-4 flex justify-between items-center">
              <span className="text-zinc-400 text-sm uppercase tracking-widest">Total</span>
              <span className="text-2xl text-white font-light">{formatPrice(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const CheckoutField = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
    <input data-testid={testid} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full bg-black border border-white/15 px-4 py-2.5 text-white text-sm mt-1 focus:border-[#2563EB] outline-none" />
  </div>
);

const IbanRow = ({ label, value, onCopy }) => (  <div className="flex items-center justify-between py-3 border-b border-white/5">
    <div>
      <p className="text-zinc-500 text-xs uppercase tracking-wider">{label}</p>
      <p className="text-white text-sm mt-1 font-mono">{value}</p>
    </div>
    {onCopy && (
      <button onClick={onCopy} className="text-zinc-500 hover:text-white transition-colors" aria-label="Copier">
        <Copy className="h-4 w-4" />
      </button>
    )}
  </div>
);
